// Card data model for Riftbound TCG.
// Mirrors the normalized fields returned by `/api/cards` and `/api/card`.
export interface RiftboundCard {
  id: string;
  name: string;
  setCode: string;
  setName?: string;
  cardType: string;          // e.g. "Unit", "Legend", "Spell", "Gear", "Battlefield"
  /**
   * All printed types when a card has more than one (e.g. Unit + Gear on
   * Patched Porobot). Prefer this for filters; `cardType` stays the primary.
   */
  cardTypes?: string[];
  /** e.g. "Champion", "Signature", "Token", "Basic" */
  supertype?: string;
  orientation?: 'portrait' | 'landscape';
  champion?: string;          // associated champion name, if any
  region?: string;            // e.g. "Demacia", "Noxus"
  regions?: string[];         // some cards belong to multiple regions
  keywords: string[];
  /** Riftcodex tags — champion name often appears here on Signature supertype cards. */
  tags?: string[];
  cost?: number;
  might?: number;
  power?: number;
  health?: number;
  rarity?: string;            // Common, Uncommon, Rare, Epic, Showcase, Promo
  /**
   * For promo printings: the gem rarity of the origin print (Rare, Epic, …).
   * Catalog `rarity` stays `Promo`; this is what the crowned rarity icon uses.
   */
  originRarity?: string;
  /** Body, Calm, Chaos, Fury, Mind, Order (`classification.domain`). */
  domain?: string[];
  /**
   * Printed Neutral champion: no domain, legal in every domain.
   * Empty `domain` alone is not this — that is also how an unrecorded domain is stored.
   */
  neutral?: boolean;
  text?: string;              // rules text (official English)
  flavor?: string;
  artist?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  number?: string;            // collector number within set (base, e.g. "26")
  /** Full Riftcodex print id: "<set>-<collectorNum>-<total>", e.g. "sfd-026a-221". */
  riftboundId?: string;
  /**
   * Printed language of this face. Absent means English.
   * Simplified Chinese judge promos use `zh` (the card prints `SC`).
   */
  language?: 'en' | 'zh' | 'ko' | 'fr';
  /**
   * Printed faces in other languages. The catalog name and rules stay English.
   * `zh` comes from the official Chinese gallery (playloltcg.com).
   * `ko` comes from the official Korean gallery (playriftbound.com/ko-kr).
   */
  locales?: {
    zh?: CardLocaleFace;
    ko?: CardLocaleFace;
  };
  /**
   * For promo printings (OPP/PR/JDG): parent product set code (OGN, SFD, VEN, …)
   * used so Set=SFD also matches Spiritforged-origin promos.
   */
  originSet?: string;
  previewed?: boolean;        // unreleased / previewed card flag
  /**
   * Signed/star Legend print (official `VEN-195*` / `ven-195-star-166`).
   * Not the playable Signature-supertype spell — that uses `supertype: 'Signature'`.
   */
  signature?: boolean;
  alternateArt?: boolean;
  raw?: Record<string, unknown>;
}

/** One printed face stored on `locales`. The catalog row itself stays English. */
export interface CardLocaleFace {
  name: string;
  /** Legend subtitle, e.g. 汹涌烈焰 on 安妮, or 폭파광 on 징크스. */
  title?: string;
  text?: string;
  flavor?: string;
  imageUrl?: string;
}

/** Face the card modal is showing. English when that printing has no overlay. */
export type CardFaceLanguage = 'en' | 'zh' | 'ko';

// Filter shape used by the sidebar / search hook.
export interface RiftboundFilter {
  cardType: string[];
  supertype: string[];
  region: string[];
  champion: string[];
  keyword: string[];
  set: string[];
  rarity: string[];
  /** Same values as `RiftboundCard.domain` / `?domain=` API param. */
  domain: string[];
  /** Creature subtypes / card-face identity tags (Dragon, Yordle, Pirate, etc.) */
  subtype: string[];
  /** Exact cost values to include (empty = any). */
  cost: number[];
  /** Exact might values to include (empty = any). */
  might: number[];
  /** Exact power-cost values to include (empty = any). */
  power: number[];
  /** Illustrator name(s) — exact match against `RiftboundCard.artist`. */
  artist: string[];
  /**
   * Print variant chips (OR): standard | altArt | overnumbered | signed | promo,
   * plus prize stamps stampChampion | stampTop8 | stampSummoner | stampRegional.
   * Empty = no restriction.
   */
  variant: string[];
  ownedOnly?: boolean;
  includePreviewed?: boolean;
  /** Inclusive display-currency bounds. Undefined means that side is open. 0 is a real minimum. */
  priceMin?: number;
  priceMax?: number;
}

export const emptyFilter: RiftboundFilter = {
  cardType: [],
  supertype: [],
  region: [],
  champion: [],
  keyword: [],
  set: [],
  rarity: [],
  domain: [],
  subtype: [],
  cost: [],
  might: [],
  power: [],
  artist: [],
  variant: [],
  ownedOnly: false,
  includePreviewed: true,
};

export interface CardSearchResponse {
  cards: RiftboundCard[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
  /**
   * Closest card-name correction when the query has no exact name substring
   * match (typo / space-slip). Present only for name searches long enough for
   * fuzzy matching. Shape matches `/api/suggest` card hits.
   */
  didYouMean?: {
    id?: string;
    name: string;
    thumbnailUrl?: string | null;
    cardType?: string | null;
    region?: string | null;
    rarity?: string | null;
  };
}

/**
 * Cached market quote for a single card variant (standard or foil),
 * returned by /api/market-quotes.
 *
 * All prices are in the listed currency (typically EUR for Cardmarket-sourced data).
 * Prices are null when not enough usable listings were found.
 */
export interface CardMarketQuote {
  cardId: string;
  provider: string;
  variant: 'standard' | 'foil' | 'best' | string;
  currency: string;
  /** Lowest listing price (outliers removed). */
  lowPrice: number | null;
  /** Median listing price — best single-number estimate. */
  medianPrice: number | null;
  /** Mean listing price. */
  meanPrice: number | null;
  /** Highest listing price (outliers removed). */
  highPrice: number | null;
  /** Number of qualifying listings used in the calculation. */
  sampleSize: number;
  /** Provider-specific product/blueprint ID. */
  providerProductId?: string | null;
  /** URL to the provider's listing page. */
  sourceUrl?: string | null;
  fetchedAt: string;
  updatedAt: string;
}

/** Official card-text errata overlay from `/api/cards?action=errata` (Neon `card_errata`). */
export type CardErrataBatch = 'origins' | 'spiritforged' | 'unleashed' | 'vendetta' | 'radiance';

export interface CardErrata {
  cardName: string;
  /** Post-errata official rules text (shown by default). */
  cardErrataText: string;
  /** As-printed / pre-errata rules text (“View original”). */
  cardOriginalText: string;
  /** Official post publish date (YYYY-MM-DD). */
  errataDate: string;
  batch: CardErrataBatch | string;
  sourceUrl?: string;
  updatedAt?: string;
}

export type CollectionVisibility = 'public' | 'private' | 'team';

/** Optional per-copy details. Absent language/condition means English / unspecified. */
export interface CollectionLot {
  quantity: number;
  language?: 'en' | 'zh' | 'ko' | 'fr';
  condition?: 'M' | 'NM' | 'LP' | 'MP' | 'HP' | 'D' | 'S';
  foil?: boolean;
  grading?: { company: string; grade: string };
  notes?: string;
}

export interface CollectionEntry {
  cardId: string;
  /** Total copies owned (foil + non-foil). Existing semantics. */
  quantity: number;
  /** Subset of `quantity` that is foil. Invariant: 0 <= quantityFoil <= quantity. */
  quantityFoil: number;
  notes?: string;
  /**
   * Optional lots that refine the rollup. `sum(lots.quantity) <= quantity`;
   * the remainder is undetailed English copies. Omit entirely for the common case.
   */
  lots?: CollectionLot[];
  addedAt: number;
  updatedAt: number;
}

export type {
  BinderAppearance,
  BinderCoverMode,
  BinderKind,
  BinderLayout,
  BinderMaterial,
  BinderModel,
  BinderPlacement,
  BinderPlacementDraft,
  UserBinder,
} from './binder';
